import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  buildOptroBaseUrl,
  defaultOptroSubdomain,
  fullOptroHost,
  isLocalhostFrontend,
  selectableOptroSuffixes,
} from "../../lib/optroDomain";
import { capturedOptroReferrer } from "../../lib/optroReferrer";
import { startOptroLogin } from "../../lib/optroLogin";

interface OptroDomainDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Sign in with Optro" domain prompt. Collects the Optro instance and resolves it
 * to a base URL, then hands off to the backend kickoff.
 *
 * Which environments the dropdown offers is deployment config
 * (`VITE_OPTRO_DOMAIN_SUFFIXES`), defaulting to the two production domains. A
 * `?signInWithOptro=1` on the URL widens it to every known environment for
 * internal testing. localhost additionally prefills `localhost:9002` and accepts
 * it as a full host, so a local frontend can point at a local or a remote Optro
 * instance.
 *
 * The field also takes a full Optro host (`acme.optroapp.ai`, or a pasted
 * instance URL), which is used as-is and makes the dropdown irrelevant. When
 * this tab arrived from an Optro instance, the field is prefilled with that
 * host, so a failed referrer auto sign-in is one click from a retry.
 */
function initialOptroInput(): string {
  const referred = capturedOptroReferrer();
  if (referred) {
    try {
      return new URL(referred).host;
    } catch {
      // Fall through to the default prefill.
    }
  }
  return defaultOptroSubdomain();
}

export function OptroDomainDialog({
  open,
  onOpenChange,
}: OptroDomainDialogProps) {
  const local = isLocalhostFrontend();
  // Both read the URL and deployment config, which cannot change while the dialog
  // is mounted; recomputing per render would rebuild the dropdown's item list on
  // every keystroke in the subdomain field.
  const suffixes = useMemo(() => selectableOptroSuffixes(), []);

  const [subdomain, setSubdomain] = useState(initialOptroInput);
  const [suffix, setSuffix] = useState<string>(() => suffixes[0]);
  const [error, setError] = useState<string | null>(null);

  // buildOptroBaseUrl resolves to the typed host when the field itself is a
  // `localhost[:port]` value or a full Optro host; otherwise it uses
  // subdomain + suffix.
  const baseUrl = buildOptroBaseUrl(subdomain, suffix, suffixes);
  const typedHost = fullOptroHost(subdomain);
  const suffixIgnored =
    typedHost !== undefined || /^localhost(:\d+)?$/i.test(subdomain.trim());

  const handleContinue = async () => {
    if (!baseUrl) {
      setError(
        typedHost === null
          ? "That isn't a recognized Optro address. Enter your subdomain (e.g. acme) or your full Optro address (e.g. acme.optroapp.ai)."
          : local
            ? "Enter localhost:9002, a subdomain (e.g. acme) with an environment, or a full Optro address."
            : "Enter your Optro subdomain (e.g. acme) or your full Optro address (e.g. acme.optroapp.ai).",
      );
      return;
    }
    // `startOptroLogin` binds the flow to this browser (the backend stores a
    // digest against the pending sign-in and the callback must present the
    // matching nonce) before handing off to the backend kickoff. `crypto.subtle`
    // is undefined outside a secure context, so an http origin that isn't
    // localhost rejects here — surface that instead of letting the click do
    // nothing.
    try {
      await startOptroLogin(baseUrl);
    } catch {
      setError(
        "Couldn't start a secure sign-in from this page. Open Midship over HTTPS and try again.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sign in with Optro</DialogTitle>
          <DialogDescription>
            {local
              ? "Use localhost:9002 for a local Optro, or a subdomain + environment for a remote one."
              : "Enter your Optro subdomain and environment, or your full Optro address."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex items-stretch gap-2">
            <Input
              value={subdomain}
              autoFocus
              placeholder={
                local
                  ? "localhost:9002 or your-company"
                  : "your-company or your-company.optroapp.ai"
              }
              onChange={(e) => {
                setSubdomain(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleContinue();
              }}
              className="flex-1"
            />

            <Select
              value={suffix}
              onValueChange={setSuffix}
              disabled={suffixIgnored}
            >
              <SelectTrigger className="w-[190px] font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {suffixes.map((s) => (
                  <SelectItem key={s} value={s} className="font-mono text-xs">
                    .{s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {baseUrl ? (
            <p className="text-xs text-gray-500">
              Connecting to <span className="font-mono">{baseUrl}</span>
            </p>
          ) : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleContinue()}
            disabled={!subdomain.trim()}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
